import * as z from 'zod'
import { inject, injectable } from '~/container'
import { PuLangFetch } from '~/lang/utils/pulangfetch'
import { CampsiteChargeTypesRepository } from './repositories'
import { chargeTypeSchema } from './schemas'
import { ChargeType, ChargeTypesInput } from './types'

const DEFAULT_NIGHTS = 2

@injectable()
export class CampsiteChargeTypesRepositoryFetch
implements CampsiteChargeTypesRepository
{
  @inject(PuLangFetch)
  private puLangFetch: PuLangFetch

  ensureInputHasNights(input: ChargeTypesInput): ChargeTypesInput {
    if (!input.depart && !input.nights) {
      return { ...input, nights: DEFAULT_NIGHTS }
    }
    return input
  }

  async get(input: ChargeTypesInput, langCode: string): Promise<ChargeType[]> {
    const cleanedInput = this.ensureInputHasNights(input)
    const response: ResponseChargeType[] =
      (await this.puLangFetch.fallbackFetch<ResponseChargeType[]>(
        langCode,
        this.getUrl(cleanedInput),
      )) || []
    return response.map((chargeType) =>
      this.mapObject(responseChargeTypeSchema.parse(chargeType), cleanedInput),
    )
  }

  private getUrl(input: ChargeTypesInput) {
    const queryParams = new URLSearchParams()
    for (const [key, value] of Object.entries(input)) {
      let mappedValue = value
      if (key === 'availability') {
        mappedValue = value ? 1 : 0
      }
      if (mappedValue) {
        queryParams.append(key, mappedValue.toString())
      }
    }
    // queryParams.append('av', process.env.ALFRED_VERSION || '')
    return `/_/endpoints/charge-types/?${queryParams.toString()}`
  }

  private mapObject(chargeType: any, input: ChargeTypesInput): ChargeType {
    return chargeTypeSchema.parse({
      ...chargeType,
      balanceDueDate: chargeType.balanceDueDate
        ? chargeType.balanceDueDate
        : undefined,
      refundUntilDate: chargeType.refundUntilDate
        ? chargeType.refundUntilDate
        : undefined,
      isNotifiable: !chargeType.isAvailable && !chargeType.isOvercapacity,
      extras: (chargeType.extras || []).map(({ extra, ...other }) => ({
        ...other,
        ...extra,
        descriptionNeedsTranslation: !!extra.descriptionNeedsTranslation,
      })),
      input,
    })
  }
}

type ResponseChargeType = z.infer<typeof responseChargeTypeSchema>

const moneySchema = z.object({
  amount: z.number(),
  amountMinor: z.number(),
  currency: z.string(),
})

const chargeTypeArrivalTaxSchema = z.object({
  name: z.string(),
  amount: moneySchema,
})

const chargeTypeExtraSchema = z.object({
  extra: z.object({
    compulsory: z.boolean(),
    description: z.string(),
    descriptionNeedsTranslation: z.boolean().optional(),
    id: z.string(),
    isVariable: z.boolean(),
    max: z.number().nullable(),
    name: z.string(),
    pricingPeriod: z.number(),
    pricingType: z.object({
      slug: z.string(),
      name: z.string(),
    }),
    terms: z.string().array(),
  }),
  price: moneySchema,
  pricingDisplay: z.string(),
})

const offerAppliedSchema = z.object({
  name: z.string(),
  discountPercent: z.string().transform(z.number(), (v) => Number(v)),
})

const quoteSchema = z.object({
  price: moneySchema,
  basePrice: moneySchema,
  preDiscountPrice: moneySchema,
  preDiscountBasePrice: moneySchema,
  preDiscountPriceAdults: moneySchema,
  preDiscountPriceChildren: moneySchema,
  preDiscountPriceInfants: moneySchema,
  adults: z.number(),
  children: z.number(),
  infants: z.number(),
  offersActive: z.boolean(),
  offersApplied: offerAppliedSchema.array(),
})

const chargeTypeStateSchema = z.enum([
  'adults_only',
  'available',
  'disabled',
  'max_capacity',
  'min_nights',
  'unavailable',
])

const chargeTypeTaxSchema = z.object({
  type: z.string(),
  inclusive: z.boolean(),
  rate: z.string().transform(z.number(), (v) => Number(v)),
  amount: moneySchema,
})

const stripePaymentWhenSchema = z.enum([
  'deferred_payment',
  'pitchup_only',
  'immediate_payment',
])

const responseChargeTypeSchema = z.object({
  arrivalTaxes: chargeTypeArrivalTaxSchema.array(),
  availAfterMinStay: z.boolean(),
  balanceDueDate: z.string().nullable(),
  chargetypeId: z.string(),
  deposit: moneySchema.nullable(),
  extras: chargeTypeExtraSchema.array().nullable(),
  isAvailable: z.boolean(),
  isOvercapacity: z.boolean(),
  minDays: z.number().nullable(),
  pitchesToSell: z.number(),
  pitchtypeId: z.string(),
  price: moneySchema.nullable(),
  quote: quoteSchema.nullable(),
  refundUntilDate: z.string().nullable(),
  remainder: moneySchema.nullable(),
  state: chargeTypeStateSchema,
  stripePaymentWhen: stripePaymentWhenSchema,
  taxes: chargeTypeTaxSchema.array(),
})
